import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import CardEditor from '../components/CardEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function DigitalCardCustomizationPage() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [customizationData, setCustomizationData] = useState({
    front: null,
    back: null,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const [restoredStates, setRestoredStates] = useState(null);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('auth_token');
    if (!token) {
      // Store the intended card ID in sessionStorage
      sessionStorage.setItem('intended_card_id', cardId);
      // Redirect to home with auth modal flag
      navigate('/?showAuth=true');
      return;
    }
    
    fetchCardDetails();
  }, [cardId, navigate]);

  const fetchCardDetails = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/public/digital-cards/${cardId}`);
      const data = await response.json();
      
      if (response.ok) {
        setCard(data);
        setLoading(false);
      } else {
        throw new Error('Failed to fetch card details');
      }
    } catch (err) {
      console.error('Failed to fetch card details:', err);
      setError('Failed to load card details');
      setLoading(false);
    }
  };

  const handleSaveCustomization = async (data) => {
    // Store both sides' customization data
    const updatedData = {
      [data.side]: {
        jsonData: data.jsonData,
        imageDataURL: data.imageDataURL,
      },
    };
    
    // Include other side data if available
    if (data.otherSideData) {
      updatedData[data.otherSide] = data.otherSideData;
    } else if (customizationData[data.otherSide]) {
      // Preserve existing other side data
      updatedData[data.otherSide] = customizationData[data.otherSide];
    }
    
    setCustomizationData(updatedData);

    // Upload the image to backend
    try {
      const formData = new FormData();
      formData.append('image', data.imageBlob, `${data.side}-customized.png`);
      formData.append('card_id', cardId);
      formData.append('side', data.side);

      const response = await fetch(`${BACKEND_URL}/api/admin/digital-cards/save-customization`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        
        // Store complete customization data (excluding canvas states for sessionStorage)
        const checkoutData = {
          cardId: card.id,
          cardName: card.name,
          cardImage: card.hero_image || card.color_variants[0]?.front_image,
          customization: {
            front: updatedData.front,
            back: updatedData.back,
            frontImageUrl: data.side === 'front' ? result.image_url : updatedData.front?.imageUrl,
            backImageUrl: data.side === 'back' ? result.image_url : updatedData.back?.imageUrl,
          },
          selectedColor: data.selectedColor,
          price: data.price,
          type: 'digital-card',
          // Store canvas states separately for restoration only (not for checkout)
          _canvasStates: data.allStates,
        };
        
        console.log('Checkout data prepared:', checkoutData);
        
        // Use React's callback to ensure state is set before showing modal
        setSavedData(checkoutData);
        // Use a small timeout to ensure React finishes the state update
        setTimeout(() => {
          setShowConfirmModal(true);
        }, 0);
      } else {
        const errorText = await response.text();
        console.error('Failed to save customization:', errorText);
        throw new Error('Failed to save customization');
      }
    } catch (err) {
      console.error('Failed to save customization:', err);
      alert(`Failed to save customization: ${err.message}. Please try again.`);
    }
  };

  const handleProceedToCheckout = () => {
    console.log('handleProceedToCheckout called');
    console.log('savedData:', savedData);
    
    if (!savedData) {
      console.error('No saved data available');
      alert('Error: No customization data found. Please try saving again.');
      return;
    }
    
    try {
      // Create a clean copy without canvas states (which may have circular refs)
      const cleanData = {
        cardId: savedData.cardId,
        cardName: savedData.cardName,
        cardImage: savedData.cardImage,
        customization: savedData.customization,
        selectedColor: savedData.selectedColor,
        price: savedData.price,
        type: savedData.type,
      };
      
      const dataToSave = JSON.stringify(cleanData);
      console.log('Clean data to save in sessionStorage:', dataToSave.substring(0, 200) + '...');
      console.log('Data length:', dataToSave.length);
      
      sessionStorage.setItem('cardCustomization', dataToSave);
      
      // Verify it was saved
      const savedCheck = sessionStorage.getItem('cardCustomization');
      console.log('Verification - data retrieved from sessionStorage:', savedCheck ? 'EXISTS (length: ' + savedCheck.length + ')' : 'NULL');
      
      console.log('Navigating to /checkout...');
      navigate('/checkout');
    } catch (error) {
      console.error('Error during checkout navigation:', error);
      alert(`Error: ${error.message}. Please try again.`);
    }
  };

  const handleKeepEditing = () => {
    setShowConfirmModal(false);
    // Restore canvas states if available
    if (savedData && savedData._canvasStates) {
      setRestoredStates(savedData._canvasStates);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading card details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-navy mb-4">Card Not Found</h2>
            <Button onClick={() => navigate('/digital-cards')} className="bg-navy text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Categories
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-12 px-4 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <Button 
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-6 text-navy hover:text-navy/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <h1 className="text-5xl font-extrabold text-navy mb-4">
            Customize Your Card
          </h1>
          <p className="text-lg text-gray-600">
            {card.name}
          </p>
        </div>
      </section>

      {/* Card Editor */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <CardEditor 
            cardData={card}
            onSave={handleSaveCustomization}
            initialStates={restoredStates}
          />
        </div>
      </section>

      <Footer />

      {/* Confirmation Modal */}
      {showConfirmModal && savedData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-navy mb-4">Review Your Design</h2>
            <p className="text-gray-600 mb-6">Please review your customized card before proceeding to checkout.</p>
            
            {/* Front and Back Preview */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Front Preview */}
              {savedData.customization.front && (
                <div>
                  <h3 className="text-lg font-semibold text-navy mb-2">Front Side</h3>
                  <div className="border-2 border-gray-300 rounded-xl overflow-hidden">
                    <img 
                      src={savedData.customization.front.imageDataURL}
                      alt="Front side preview"
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              {/* Back Preview */}
              {savedData.customization.back && (
                <div>
                  <h3 className="text-lg font-semibold text-navy mb-2">Back Side</h3>
                  <div className="border-2 border-gray-300 rounded-xl overflow-hidden">
                    <img 
                      src={savedData.customization.back.imageDataURL}
                      alt="Back side preview"
                      className="w-full"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Card Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-navy mb-2">{savedData.cardName}</h3>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                {savedData.selectedColor && (
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: savedData.selectedColor.code }}
                    ></div>
                    <span>{savedData.selectedColor.name}</span>
                  </div>
                )}
                <span className="font-bold text-navy">${savedData.price}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleKeepEditing}
                variant="outline"
                className="flex-1 py-6 text-base"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Keep Editing
              </Button>
              <Button
                onClick={handleProceedToCheckout}
                className="flex-1 bg-navy text-white hover:bg-navy/90 py-6 text-base"
              >
                Proceed to Checkout
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
